
import { Vue, Component, Watch } from "vue-property-decorator";
import { validate as uuidValidate } from "uuid";
import { configStore, globalStore, uiStore } from "@/store/store";
import {
  addSelectorToStore,
  createSelectorFromName,
  setElementSelectors
} from "@/oxygen/classes/lib/classes";
import NiceButton from "@/components/UI/NiceButton.vue";
import Icon from "@/components/UI/Icon.vue";
import OxyClassToken from "@/oxygen/classes/OxyClassToken.vue";
import slugify from "slugify";
import { isValidClassName } from "@/lib/selectors/selectors";
import OxygenCopyPastContextMenu from "@/oxygen/properties-panel/OxygenCopyPastContextMenu.vue";

@Component({
  components: {
    OxygenCopyPastContextMenu,
    Icon,
    NiceButton,
    OxyClassToken
  }
})
export default class ClassDropdown extends Vue {
  newClassName: string | null = null;
  errorMessage: string | null = null;

  @Watch("newClassName")
  onNewClassNameChange() {
    // Reset error message when user starts typing
    if (this.newClassName) {
      this.errorMessage = null;
    }
  }

  @Watch("elementId", { immediate: true })
  onElementIdChange() {
    if (this.activeSelector) {
      uiStore.setCurrentElementTab("classes");
      uiStore.setCurrentElementOxyNestedSelector(undefined);
    }
  }

  get element() {
    return uiStore.activeElement;
  }

  get elementId() {
    return uiStore.activeElementId;
  }

  get availableClasses() {
    return globalStore.oxySelectors.filter(e => e.type === "class");
  }

  get activeSelector() {
    return uiStore.activeOxySelector;
  }

  get activeNestedSelector() {
    return uiStore.activeOxyNestedSelector;
  }

  get elementClasses(): string[] {
    if (!this.element) return [];
    return this.element?.data?.properties?.meta?.classes || [];
  }

  get showSuggestClassName() {
    const existingClassNames = this.elementClasses
      .map(e => this.availableClasses.find(c => c.id == e))
      .map(e => e?.name)
      .filter(e => e);

    const alreadyContainsASimilarClass = existingClassNames.some(e =>
      e?.match(new RegExp(`^${this.elementSlug}-\\d+$`))
    );

    return !this.newClassName && !alreadyContainsASimilarClass;
  }

  get elementSlug() {
    if (!this.element) return "";

    const element = configStore.element(this.element.data.type);
    return slugify(element.name, { lower: true });
  }

  get suggestClassName() {
    const namesArray = this.availableClasses.map(e => e.name);

    let count = 1;
    while (namesArray.includes(`${this.elementSlug}-${count}`)) {
      count++;
    }

    return `${this.elementSlug}-${count}`;
  }

  addSuggestedClass() {
    this.updateClasses([...this.elementClasses, this.suggestClassName]);
  }

  createClassIfNeeded(className: string) {
    const foundClass = this.availableClasses.find(e => e.name == className);

    if (foundClass) return foundClass;

    const cssClass = createSelectorFromName(className);

    addSelectorToStore(cssClass);

    return cssClass;
  }

  createFromNameAndSelect() {
    if (!this.newClassName) return;
    const cssClass = this.createClassIfNeeded(this.newClassName);
    this.activateSelector(cssClass.id);
  }

  updateClasses(classes?: string[]) {
    if (!this.elementId) return;

    if (!classes) {
      setElementSelectors(this.elementId, []);
      return;
    }

    this.errorMessage = null;

    const newClassName = classes.find(e => !uuidValidate(e));
    const existingClasses = classes.filter(e => uuidValidate(e));

    if (newClassName) {
      if (!isValidClassName(`.${newClassName}`)) {
        // Reset the input value to the previous state as v-combobox mutates the value
        setElementSelectors(this.elementId, existingClasses);
        this.errorMessage = "Invalid class name";
        return;
      }

      const classId = this.createClassIfNeeded(newClassName).id;

      if (!this.elementClasses.includes(classId)) {
        existingClasses.push(classId);
      }
    }

    setElementSelectors(this.elementId, existingClasses);

    this.activateSelector(existingClasses[existingClasses.length - 1]);
  }

  activateSelector(id: string) {
    if (!this.elementId) return;

    uiStore.setCurrentElementOxySelector(id);
    uiStore.setCurrentElementTab("classes");

    this.closeMenu();
  }

  clearSelector() {
    uiStore.setCurrentElementOxySelector(undefined);
    uiStore.setCurrentElementTab(undefined);
  }

  selectNestedSelector(id: string) {
    uiStore.setCurrentElementOxyNestedSelector(id);
  }

  clearNestedSelector() {
    uiStore.setCurrentElementOxyNestedSelector(undefined);
  }

  closeMenu() {
    // @ts-ignore
    this.$refs.input!.isMenuActive = false;
    // @ts-ignore
    this.$refs.input!.blur();
  }

  onBlur(event: MouseEvent) {
    const target = event.relatedTarget as HTMLElement;

    if (target?.classList.contains("v-list-item")) return;

    this.newClassName = "";
  }

  getNestedSelectorForActiveSelector(selectorId: string) {
    return selectorId === this.activeSelector?.id
      ? this.activeNestedSelector
      : undefined;
  }
}
